import type { SummaryTable, TableItemProps } from '@elfsight/widget-checkout';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useTables } from '../../tables-context';
import { getDiscount } from '../../utils';
import { useFormPopup } from '../form-popup-context';

type TableItems = Omit<TableItemProps, 'layout' | 'handleValueResize'>[];

export const useSummaryTables = (): SummaryTable[] => {
  const { t } = useLingui();

  const { layout } = useSettings();

  const { activeTable } = useTables();
  const { columns = [] } = activeTable ?? {};
  const withHeadFeatures = layout === 'comparisonTable';
  const { features: headFeatures } = withHeadFeatures ? columns[0] : {};

  const { currentColumn } = useFormPopup();
  if (!currentColumn) {
    return [];
  }

  const { features, price, oldPrice, discount } = currentColumn;

  const featureItems: TableItems = features.map(({ text: value }, index) => ({
    label: headFeatures?.[index]?.text,
    values: [{ value }]
  }));

  const { enabled: oldPriceEnabled } = oldPrice;
  const { enabled: discountEnabled, discountType, customLabel } = discount;
  const paymentItems: TableItems = [];

  if (oldPriceEnabled) {
    paymentItems.push({
      label: t`Old Price`,
      values: [{ price: { ...price, ...oldPrice } }]
    });
  }

  if (discountEnabled) {
    paymentItems.push({
      label: t`Discount`,
      values: [
        {
          value:
            discountType === 'custom'
              ? customLabel
              : getDiscount({ discount, oldPrice, newPrice: price })
        }
      ]
    });
  }

  return [
    ...(featureItems.length ? [{ items: featureItems }] : []),
    ...(paymentItems.length
      ? [{ items: paymentItems, layout: 'horizontal' as const }]
      : [])
  ];
};
